import { defineStore } from 'pinia';

import type { FragmentType } from '~/graphql/generated/fragment-masking';

import { authUserFragment, selfQuery } from '~/graphql/documents/core';
import { useFragment } from '~/graphql/generated/fragment-masking';

type UserType = FragmentType<typeof authUserFragment>;

export const useAuthStore = defineStore('auth', () => {
  // Read-only: the server sets/clears this marker via Set-Cookie (AttachAuthMarkerCookie).
  // A JS write here would be subject to WebKit ITP's script-writable cookie cap.
  // Cluster isolation is by the `${code}__` name prefix (useClusterCookie).
  const authCookie = useClusterCookie<string | undefined>('auth-marker', { sameSite: 'lax', secure: true });
  const currentSchoolId = useClusterCookie<string | undefined>('current-school-id');

  const serverEvent = useRequestEvent();
  const guestId = useClusterCookie('guest-id', { default: () => serverEvent?.context['guest-id'] || crypto.randomUUID() });

  const configStore = useConfigStore();

  const { $urqlClient } = useNuxtApp();

  const isloggingOut = ref(false);

  // Internal state holds masked fragment data; actual typed value is accessed via computed `user`
  const _user = ref<unknown>(null);

  const user = computed(() => {
    return useFragment(authUserFragment, <FragmentType<typeof authUserFragment>>_user.value);
  });

  const setUser = (newUser: UserType | null) => {
    _user.value = newUser;
  };

  const isLoggedIn = computed(() => {
    return !!user.value;
  });

  const fetchUser = async (networkOnly = false) => {
    const res = await $urqlClient.query(selfQuery, {}, { requestPolicy: networkOnly ? 'network-only' : 'cache-first' });

    if (res.data?.core && !res.error) {
      if (res.data.core.user.self) {
        setUser(res.data.core.user.self);
      }

      return;
    }

    if (!res.error) {
      return;
    }

    const unauthenticatedError = findGraphqlErrorWithCode(res.error, 'UNAUTHENTICATED');

    if (unauthenticatedError) {
      return;
    }

    throw createError({
      statusCode: 500,
      statusMessage: 'Could not fetch user',
      data: res.error,
      fatal: true,
    });
  };

  const checkForAuthUser = async () => {
    if (authCookie.value === 'authenticated') {
      await fetchUser();
    }
  };

  const unsubscribe: Ref<null | (() => void)> = ref(null);

  if (import.meta.client) {
    watchEffect(() => {
      if (user.value) {
        if (!unsubscribe.value) {
          unsubscribe.value = $urqlClient.query(selfQuery, {}).subscribe((res) => {
            setUser(res.data?.core.user.self || null);
          }).unsubscribe;
        }
      }
      else {
        if (unsubscribe.value) {
          unsubscribe.value();
          unsubscribe.value = null;
        }
      }
    });
  }

  const uiStore = useUiStore();

  const reset = () => {
    setUser(null);
    currentSchoolId.value = undefined;
    uiStore.resetUiStore();
  };

  const sessionTimeout = (configStore.publicConfig?.sessionLifetime ?? Infinity) * 60 * 1000;

  const expiry = ref<number>(Date.now() + sessionTimeout);

  const refreshExpiry = () => {
    expiry.value = Date.now() + sessionTimeout;
  };

  const authId = computed(() => user.value?.id || guestId.value);

  return {
    user,
    setUser,
    isLoggedIn,
    isloggingOut,
    authCookie,
    guestId,
    checkForAuthUser,
    reset,
    currentSchoolId,
    fetchUser,
    expiry,
    refreshExpiry,
    authId,
  };
});
